import React, { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import SubmitButton from '../../../../../components/submit-button';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  shopNShipDetailsState,
  chinaImportState,
  filteredShopNShipLinksState,
  saveShopNShipRateState,
  selectedRateState,
  shopnshipSelectedCountryState
} from '../../../../../recoil/atoms';
import BusyOverlay from '../../../../../components/busy-overlay';
import NewShopNShipStepsLayout from '../../../../../components/Layouts/shop-n-ship-steps-layout';
import { useHistory } from 'react-router-dom';
import { useSendAlert } from '../../../../../utilities/send-alert';
import testJSON from '../../../../../utilities/test-json';
import { useGetShopNShipRateQuery } from '../../../../../operations/queries';
import insurancePlans from '../../../../../constants/insurance-plans';
import { WhiteCheck } from '../../../../../constants/asset-contants';
import {
  DeliveryOptions2,
  InsuranceOptions
} from '../../../../../components/shipping-options';

const ShopNShipOtherInformation = () => {
  const [shopNshipDetails, setSaveShopNshipDetails] = useRecoilState(
    shopNShipDetailsState
  );
  const [isChinaImport] = useRecoilState(chinaImportState);
  const isArray = testJSON(shopNshipDetails?.itemDescription);
  const [getShopNshipRates, { loading }] = useGetShopNShipRateQuery({});
  const shopNShipRates = useRecoilValue(saveShopNShipRateState);
  const [selectedRate, setSelectedRate] = useRecoilState(selectedRateState);
  const activeRate = shopNShipRates.filter(
    rate => rate.pricingTier === shopNshipDetails?.pricingTier
  );
  const selectedCountry = useRecoilValue(shopnshipSelectedCountryState);
  const totalItemValue =
    (isArray
      ? JSON.parse(shopNshipDetails?.itemDescription)?.reduce(
          (sum, item) => sum + item?.amountDue,
          0
        )
      : shopNshipDetails?.amountDue) || 0;

  const onCompleted = data => {
    const items = JSON.parse(shopNshipDetails?.itemDescription);

    // we are joining all tracking numbers for backward compatibility (old shopnship did not have multiple items)
    const trackingNumber = isArray
      ? items?.map(item => item?.trackingNumber)?.join()
      : shopNshipDetails?.trackingNumber;

    // we are joining all proofs of id for backward compatibility (old shopnship did not have multiple items)
    const proofOfId = isArray
      ? items.map(item => item?.proofOfId)?.join()
      : shopNshipDetails?.proofOfId;

    // we are joining all evidences of purchase for backward compatibility (old shopnship did not have multiple items)
    const evidenceOfPurchase = isArray
      ? items.map(item => item?.evidenceOfPurchase)?.join()
      : shopNshipDetails?.evidenceOfPurchase;

    setSaveShopNshipDetails({
      ...shopNshipDetails,
      ...values,
      proofOfId,
      trackingNumber,
      evidenceOfPurchase,
      // for normal shopnship, all necessary math is done on the backend and sent to frontend here.
      ...(!isChinaImport && {
        initialCharge: activeRate?.[0]?.cost,
        foreignExchangeCost: activeRate?.[0]?.foreignExchangeCost,
        foreignExchangeRate: activeRate?.[0]?.foreignExchangeRate
      })
    });

    history.push(filteredLinks[currentIndex + 1].link);
  };

  const history = useHistory();
  const sendAlert = useSendAlert();

  const [filteredLinks] = useRecoilState(filteredShopNShipLinksState);
  const currentIndex = filteredLinks.findIndex(
    link => link.link === history.location.pathname
  );
  // const loadingState = getShopnshipRatesResult.isLoading;
  const [isChecked, setIsChecked] = useState(false);
  const validationSchema = yup.object().shape({
    receiverPaymentDetails: yup.boolean().required('Please select an option'),
    uploadingMyDocuments: yup.boolean().required('Please select an option')
  });

  const { values, touched, errors, handleSubmit, setFieldValue } = useFormik({
    validationSchema,
    enableReinitialize: true,
    initialValues: {
      insuranceType: '',
      insuranceCharge: 0,
      uploadingMyDocuments: true,
      receiverPaymentDetails: true
    },

    onSubmit: values => {
      if (isChinaImport) {
        if (isChecked) {
          delete values.insuranceType;
          delete values.insuranceCharge;

          setSaveShopNshipDetails({ ...shopNshipDetails, ...values });
          history.push(filteredLinks[currentIndex + 1].link);
        }
      } else {
        if (!values.insuranceType) {
          sendAlert('Please select your Insurance Type', 'error');
          window.scroll(0, 0);
        } else {
          onCompleted();
        }
      }
    }
  });

  const handleStateChange = (name, value) => {
    setFieldValue(name, value);
  };

  const handleInsuranceSelection = plan => {
    const insuranceCharge =
      plan.value === insurancePlans[2].value
        ? totalItemValue * 0.05
        : plan.amount;

    setFieldValue('insuranceType', plan.value);
    setFieldValue('insuranceCharge', insuranceCharge);
  };

  useEffect(() => {
    if (!isChinaImport) {
      // what this does is to autofill the form for this page from the existing values stored in local storage through recoil
      const keys = Object.keys(shopNshipDetails);
      const _values = Object.values(shopNshipDetails);
      const formKeys = Object.keys(values);
      _values.forEach((value, index) => {
        const formKey = formKeys.find(key => keys[index] === key);
        if (shopNshipDetails[keys[index]] === value && formKey) {
          setFieldValue(
            keys[index],
            keys[index] === 'insuranceCharge'
              ? value > insurancePlans[1].amount
                ? value / 100
                : value
              : value
          );
        }
      });
    }
    // eslint-disable-next-line
  }, [isChinaImport]);

  useEffect(() => {
    if (shopNShipRates) {
      const activeRates = shopNShipRates.filter(
        rate => rate.pricingTier === shopNshipDetails?.pricingTier
      );
      setSelectedRate(activeRates[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopNShipRates]);

  useEffect(() => {
    if (!isChinaImport) {
      const items = JSON.parse(shopNshipDetails?.itemDescription);
      const requestRatesData = items.map(item => {
        return { weight: item.estimatedWeight, category: item.category };
      });
      const params = {
        shippingFrom: shopNshipDetails?.shippingFrom?.slice(0, 2),
        items: requestRatesData
      };
      getShopNshipRates(params);
    }

    // eslint-disable-next-line
  }, [selectedCountry?.value, isChinaImport]);

  return (
    <NewShopNShipStepsLayout mobileTitle='Other Information'>
      <div className='new-shop-n-ship__modal__body'>
        <div className='new-shop-n-ship__modal__body__wrap'>
          <div className='new-shop-n-ship__modal__body__content'>
            <form
              onSubmit={handleSubmit}
              className='new-shop-n-ship__modal__body__form'
            >
              {isChinaImport ? null : (
                <div className='delivery-options mt-5'>
                  <div className=''>
                    <DeliveryOptions2
                      loading={loading}
                      shopNShipRates={activeRate}
                      selectedRate={selectedRate}
                    />
                  </div>
                  <div>
                    <InsuranceOptions
                      insurancePlans={insurancePlans}
                      totalItemValue={totalItemValue}
                      values={values}
                      handleInsuranceSelection={handleInsuranceSelection}
                      shopNshipDetails={shopNshipDetails}
                    />
                  </div>
                </div>
              )}
              <Grid container rowSpacing='16px' columnSpacing='10px'>
                <div id='input-control'>
                  <label htmlFor='receiverPaymentDetails'>
                    <span>Was payment made using receiver details? </span>
                  </label>
                  <div className='radio-group'>
                    <div className='radio-group-option'>
                      <input
                        type='radio'
                        id='receiverPaymentDetailsTrue'
                        name='receiverPaymentDetails'
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('receiverPaymentDetails', true)
                        }
                        disabled={!!shopNshipDetails.id}
                        checked={values?.receiverPaymentDetails === true}
                      />
                      <span
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('receiverPaymentDetails', true)
                        }
                      >
                        Yes I did
                      </span>
                    </div>
                    <div className='radio-group-option'>
                      <input
                        type='radio'
                        id='receiverPaymentDetailsFalse'
                        name='receiverPaymentDetails'
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('receiverPaymentDetails', false)
                        }
                        disabled={!!shopNshipDetails.id}
                        checked={values?.receiverPaymentDetails === false}
                      />
                      <span
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('receiverPaymentDetails', false)
                        }
                      >
                        No, it was purchased by someone else
                      </span>
                    </div>
                  </div>
                  <small>
                    {touched.receiverPaymentDetails &&
                      errors.receiverPaymentDetails}
                  </small>
                </div>
                <div id='input-control'>
                  <label htmlFor='uploadingMyDocuments'>
                    <span>Document Upload </span>
                  </label>
                  <div className='radio-group'>
                    <div className='radio-group-option'>
                      <input
                        type='radio'
                        id='uploadingMyDocumentsTrue'
                        name='uploadingMyDocuments'
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('uploadingMyDocuments', true)
                        }
                        disabled={!!shopNshipDetails.id}
                        checked={JSON.parse(values?.uploadingMyDocuments)}
                      />
                      <span
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('uploadingMyDocuments', true)
                        }
                      >
                        I am Uploading my documents
                      </span>
                    </div>
                    <div className='radio-group-option'>
                      <input
                        type='radio'
                        id='uploadingMyDocumentsFalse'
                        name='uploadingMyDocuments'
                        disabled={!!shopNshipDetails.id}
                        checked={!JSON.parse(values?.uploadingMyDocuments)}
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('uploadingMyDocuments', false)
                        }
                      />
                      <span
                        onClick={() =>
                          !shopNshipDetails?.id &&
                          handleStateChange('uploadingMyDocuments', false)
                        }
                      >
                        I am uploading someone else’s documents
                      </span>
                    </div>
                  </div>
                  <small>
                    {touched.uploadingMyDocuments &&
                      errors.uploadingMyDocuments}
                  </small>
                </div>
              </Grid>
              <div className='mt-5'>
                <div
                  className={`verification__content
                     
                    `}
                >
                  <div
                    style={{
                      textAlign: 'left'
                    }}
                    className='verification__header text-left'
                  >
                    <p
                      style={{
                        fontSize: '14px',
                        lineHeight: '24px'
                      }}
                    >
                      I certify that I’m using this for genuine purposes; else,
                      Topship may involve law enforcement agencies if faulted.
                      And I do understand that knowingly making any false or
                      fraudulent representations to any online businesses may
                      violate federal or state criminal statutes, and may result
                      in a fine, imprisonment, or both.
                    </p>
                  </div>

                  <div
                    className='mt-2'
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => setIsChecked(!isChecked)}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        backgroundColor: isChecked ? '#6B50F6' : 'transparent',
                        border: '2px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '8px'
                      }}
                    >
                      {isChecked && (
                        <img width={20} src={WhiteCheck} alt='white check' />
                      )}
                    </div>
                    <label
                      className='mb-0'
                      style={{
                        color: 'black',
                        fontSize: '14px',
                        marginLeft: '10px'
                      }}
                    >
                      I Agree to the above terms
                    </label>
                  </div>
                </div>
              </div>
              <div className='new-shop-n-ship__modal__body__form__item__buttons'>
                <div
                  className={`new-shop-n-ship__modal__body__form__item__button${
                    shopNshipDetails.id ? ' disabled' : ''
                  }`}
                  onClick={() =>
                    !shopNshipDetails.id &&
                    history.push(filteredLinks[currentIndex - 1].link)
                  }
                >
                  Go Back
                </div>

                <SubmitButton disabled={!isChecked} text={`Next`} />
              </div>
            </form>
          </div>
        </div>
      </div>
      <BusyOverlay loading={false} speed='1.7s' />
    </NewShopNShipStepsLayout>
  );
};

export default ShopNShipOtherInformation;
